import ToolTip from 'components/global/ToolTip';
import { deepMerge } from 'toolbox/deepMerge';
import { Event } from 'services/EventEmitter';
import { throttle } from 'toolbox/throttle';

/* eslint-disable complexity */
/* eslint max-depth: ["error", 8] */

/**
 * This is a description of the Toolkit constructor function.
 * @class
 * @classdesc This is a description of the Toolkit class. (must be edited)
 * @extends Component
 */
export default class Toolkit extends ToolTip {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            debugKey: 'Shift', // Hold this key and hover a component with mouse to display the debug information
            freezeKey: 'Alt', // Press this key to freeze the tooltip with debug information
            debugModeShortcut: { // Default toolkit enabling shortcut: CTRL + SHIFT + F12
                ctrlKey: true,
                altKey: false,
                shiftKey: true,
                key: 'F12',
            },
            hideTooltipOnMouseLeave: false,
        }, options));
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        super.initState();

        this.state.isToolkitEnabled = false;
        this.state.isDebugKeyPressed = false;
        this.state.isDetailsFrozen = false;
        this.state.currentUUID = null;
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        document.addEventListener('keydown', this.onKeydown.bind(this));
        document.addEventListener('keyup', this.onKeyup.bind(this));
        document.addEventListener('mousemove', throttle(this.onMousemove.bind(this), 200));
    }

    /**
     * Mousemove event handler
     * @param {Object} event dom event
     */
    onMousemove(event) {
        if (!this.state.isDebugKeyPressed || this.state.isDetailsFrozen) {
            return;
        }

        const x = event.clientX;
        const y = event.clientY;
        let element = document.elementFromPoint(x, y);

        if (!element) {
            return;
        }

        if (!element.hasAttribute('[data-ngl-debug-uuid]')) {
            element = element.closest('[data-ngl-debug-uuid]');
        }

        const uuid = (element && element.getAttribute('data-ngl-debug-uuid')) || null;

        // hide content if no selected component or it is not equal to current one
        if (this.state.currentUUID !== uuid) {
            this.state.hasContent = false;
            if (!this.state.isDetailsFrozen) {
                this.hideToolkitTooltip();
            }
        }

        this.state.currentUUID = uuid;

        if (!this.state.currentUUID) {
            return;
        }

        const tree = this.retrieveParentTree(element);
        const meta = window.nglDebugMeta[uuid];
        let content = `<p><strong>Component: </strong>${meta.nglComponent}</p>`;

        if (tree.length) {
            content += '<p><strong>Components tree</strong></p>';
            content += '<ul>';
            tree.forEach((itemMeta) => {
                content += `<li>${itemMeta.nglComponent}</li>`;
            });
            content += '</ul>';
        }
        this.options.text = content;
        this.hideToolkitTooltip();
        this.selectors.target = element;
        this.highlight(this.selectors.target);
        super.showContent();
    }

    /**
     * Keydown event handler
     * @param {Object} event dom event
     */
    onKeydown(event) {
        if (this.options.debugModeShortcut.ctrlKey === event.ctrlKey
            && this.options.debugModeShortcut.altKey === event.altKey
            && this.options.debugModeShortcut.shiftKey === event.shiftKey
            && this.options.debugModeShortcut.key === event.key) {
            // Toggle developer toolkit debug mode
            this.state.isToolkitEnabled = !this.state.isToolkitEnabled;

            if (this.state.isToolkitEnabled) {
                Event.emit('snackbar.notify', { content: 'Developer toolkit is enabled', type: 'success' });
                this.element.classList.add('m-visible');
            } else {
                Event.emit('snackbar.notify', { content: 'Developer toolkit is disabled', type: 'warning' });
                this.element.classList.remove('m-visible');
            }
        }

        if (this.state.isToolkitEnabled && event.key === this.options.debugKey) {
            if (!this.state.isDebugKeyPressed) {
                this.initMeta();
            }

            this.state.isDebugKeyPressed = true;
        }

        if (this.state.isToolkitEnabled
            && this.state.isDebugKeyPressed && event.key === this.options.freezeKey) {
            this.hideToolkitTooltip();
            this.state.isFocusDisabled = false;
            this.highlight(this.selectors.target);
            super.showContent();
            this.state.isDetailsFrozen = true;
        }
    }

    /**
     * Keyup event handler
     * @param {Object} event DOM event
     */
    onKeyup(event) {
        if ((event.key === this.options.debugKey && !this.state.isDetailsFrozen) || event.key === 'Escape') {
            this.hideToolkitTooltip();
        }
    }

    /**
     * Tooltip Close click event handler
     */
    onTooltipCloseClick() {
        this.hideToolkitTooltip();
    }

    /**
     * Hide toolkit tooltip
     * @param {Object} event DOM event
     */
    hideToolkitTooltip() {
        this.state.isDebugKeyPressed = false;
        this.state.isDetailsFrozen = false;

        super.hideContent();
        super.unbindCustomTooltipEvents();
        this.unhighlight(this.selectors.target);

        this.state.isFocusDisabled = true;
    }

    /**
     * Retrieves parent meta data commonet
     * @param {Node} node DOM node
     * @returns {Node} parent commone node with tooklit metadata
     */
    retrieveParent(node) {
        // list of comment nodes which should not be taken into account
        const skipNodes = {};
        // get prev node
        let prevNode = node.previousSibling || node.parentNode;

        // iterate from the current node up the tree
        while (prevNode) {
            // check if node is comment node with NGL meta data
            if (prevNode.nodeType === Node.COMMENT_NODE && prevNode.data && prevNode.data.indexOf('nglID') > -1) {
                const meta = this.parseMeta(prevNode.data);
                const uuid = meta.nglID;

                // if "end" comment found first, skip its start, because comments on same level but not parent
                if (meta.nglTag === 'end') {
                    skipNodes[uuid] = true;
                } else if (meta.nglTag === 'begin' && !skipNodes[uuid]) {
                    // return comment node with debug meta
                    return prevNode;
                }
            }

            // retrieve next node
            prevNode = prevNode.previousSibling || prevNode.parentNode;
        }

        return null;
    }

    /**
     * Return list of parent meta data objects
     * @param {Node} element DOM element
     * @returns {array} list of parent meta data objects
     */
    retrieveParentTree(element) {
        const tree = [];
        let node = this.retrieveParent(element);

        while (node) {
            // build list of parent components
            tree.unshift(this.parseMeta(node.data));
            node = this.retrieveParent(node);
        }

        return tree;
    }

    /**
     * Tooltip method overload to unfreeze modal-like tooltip
     */
    hideContent() {
        this.hideToolkitTooltip();
    }

    /**
     * Initialize component meta data
     */
    initMeta() {
        const treeWalker = document.createTreeWalker(document.body, NodeFilter.SHOW_COMMENT, null, false);
        // set global debug meta
        window.nglDebugMeta = {};

        while (treeWalker.nextNode()) {
            const { currentNode } = treeWalker;

            if (!currentNode.data || currentNode.data.indexOf('nglID') === -1) {
                continue; // eslint-disable-line no-continue
            }

            const meta = this.parseMeta(currentNode.data);
            const uuid = meta.nglID;

            // skip end tag processing
            if (meta.nglTag === 'end') {
                continue; // eslint-disable-line no-continue
            }

            // find content between two begin/end comments
            let sibling = currentNode.nextSibling;

            while (sibling) {
                // check if this is end of current component
                if (sibling.nodeType === Node.COMMENT_NODE) {
                    let siblingUUID = null;

                    if (sibling.data && sibling.data.indexOf('nglID') > -1) {
                        const siblingMeta = this.parseMeta(sibling.data);
                        siblingUUID = siblingMeta.nglID;
                    }

                    // compare uuid of comments
                    if (uuid && uuid === siblingUUID) {
                        // prevent execution if end found
                        break;
                    }
                } else if (sibling.nodeType === Node.ELEMENT_NODE || sibling.nodeType === Node.TEXT_NODE) {
                    let target = null;
                    // if node is pure text, wrap it in span, otherwise we will not be able to add "mouseenter" event listener
                    if (sibling.nodeType === Node.TEXT_NODE) {
                        const parent = sibling.parentNode;

                        if (parent && sibling.nodeValue && sibling.nodeValue.trim()) {
                            // create span and replace text node with it
                            const span = document.createElement('span');
                            span.innerHTML = sibling.nodeValue;
                            parent.replaceChild(span, sibling);
                            target = span;
                        }
                    } else {
                        target = currentNode.nextElementSibling;
                    }

                    // do no assign new event listeners if element already has debug information
                    if (target) {
                        // set debug uuid to element
                        target.setAttribute('data-ngl-debug-uuid', uuid);
                        window.nglDebugMeta[uuid] = meta;
                    }
                }

                // go to next sibling if end was not reached
                sibling = sibling.nextSibling;
            }
        }
    }

    /**
     * Highlight element
     * @param {Element} element dom element to be highlighted
     */
    highlight(element) {
        if (!this.selectors.target) {
            return;
        }

        let targetWidth;
        let targetLeft;
        const borderWidth = 3;
        const targetRectangle = element.getBoundingClientRect();
        const targetRectangleLeft = targetRectangle.left + window.scrollX;
        const targetTop = targetRectangle.top + window.scrollY - (borderWidth * 2);
        const targetHeight = targetRectangle.height + (borderWidth * 2);

        // Check if target element full width
        if (targetRectangle.width >= document.body.clientWidth) {
            targetWidth = targetRectangle.width - (borderWidth * 4);
            targetLeft = borderWidth;
        // Check if target element too close to left side
        } else if (targetRectangleLeft < borderWidth * 2) {
            targetWidth = targetRectangle.width - (borderWidth * 2);
            targetLeft = borderWidth;
        // Check if target element too close to right side
        } else if (targetRectangleLeft + targetRectangle.width + (borderWidth * 2)
            >= document.body.clientWidth) {
            targetWidth = targetRectangle.width - (borderWidth * 2);
            targetLeft = document.body.clientWidth - targetRectangle.width - borderWidth;
        } else {
            targetWidth = targetRectangle.width + (borderWidth * 2);
            targetLeft = targetRectangleLeft - (borderWidth * 2);
        }

        const highlighterStyle = this.element.style;

        highlighterStyle.top = `${targetTop}px`;
        highlighterStyle.left = `${targetLeft}px`;
        highlighterStyle.width = `${targetWidth >= 0 ? targetWidth : 0}px`;
        highlighterStyle.height = `${targetHeight}px`;
        highlighterStyle.opacity = '1';
    }

    /**
     * Unhighlight element
     * @param {Element} element dom element to be unhighlighted
     */
    unhighlight() {
        if (!this.selectors.target) {
            return;
        }

        const highlighterStyle = this.element.style;

        highlighterStyle.width = '0';
        highlighterStyle.height = '0';
        highlighterStyle.opacity = '0';
    }

    /**
     * Parse meta data into object
     * @param {String} data meta data to be parsed
     * @returns {Object} toolkit meta
     */
    parseMeta(data) {
        if (!data) {
            return {};
        }
        const meta = {};

        data.split(':').forEach((item) => {
            const parts = item.split('=').map(str => str.trim());

            if (parts && parts.length === 2) {
                const [key, value] = parts;
                meta[key] = value;
            }
        });

        return meta;
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        super.destroy();

        document.removeEventListener('keydown', this.onKeydown.bind(this));
        document.removeEventListener('keyup', this.onKeyup.bind(this));
        document.removeEventListener('mousemove', this.onMousemove.bind(this));
    }
}
